import React, { useState, useEffect } from 'react';
import { collection, query, getDocs, where } from 'firebase/firestore';
import { db } from '../firebase';

const EmployeeDocumentsModule = ({ employee, serverId, canEdit }) => {
    const [activeTab, setActiveTab] = useState('documentCloud');

    // Data states
    const [documents, setDocuments] = useState([]);
    const [riskAssessments, setRiskAssessments] = useState([]);
    const [loading, setLoading] = useState(false);
    
    // Folder navigation state
    const [currentFolderId, setCurrentFolderId] = useState(null);
    const [breadcrumbs, setBreadcrumbs] = useState([{ id: null, name: 'Root' }]);

    useEffect(() => {
        if (!employee?.username || !serverId) return;

        const fetchData = async () => {
            setLoading(true);
            try {
                if (activeTab === 'documentCloud') {
                    const docsRef = collection(db, 'servers', serverId, 'employees', employee.username, 'documents');
                    const q = query(docsRef, where('deletedAt', '==', null), where('archivedAt', '==', null));
                    const snapshot = await getDocs(q);
                    
                    let docs = [];
                    snapshot.forEach(doc => {
                        docs.push({ id: doc.id, ...doc.data() });
                    });
                    
                    // Sort folders first, then by name
                    docs.sort((a, b) => {
                        const aIsFolder = a.contentType === 'folder';
                        const bIsFolder = b.contentType === 'folder';
                        if (aIsFolder && !bIsFolder) return -1;
                        if (!aIsFolder && bIsFolder) return 1;
                        return (a.name || '').localeCompare(b.name || '');
                    });
                    
                    setDocuments(docs);
                } else if (activeTab === 'riskAssessments') {
                    const risksRef = collection(db, 'servers', serverId, 'employees', employee.username, 'riskAssessment');
                    const snapshot = await getDocs(risksRef);
                    let risks = [];
                    snapshot.forEach(doc => {
                        risks.push({ id: doc.id, ...doc.data() });
                    });
                    
                    risks.sort((a, b) => {
                        const dateA = a.createdAt?.toDate() || new Date(0);
                        const dateB = b.createdAt?.toDate() || new Date(0);
                        return dateB - dateA; // Descending
                    });
                    
                    setRiskAssessments(risks);
                }
            } catch (error) {
                console.error("Error fetching document data:", error);
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [activeTab, employee?.username, serverId]);

    // Derived state for Document Cloud
    const currentDocuments = documents.filter(doc => {
        const parent = doc.parentId;
        if (!currentFolderId) {
            return !parent; // Root level (null or undefined or empty string)
        }
        return parent === currentFolderId;
    });

    const handleFolderClick = (folder) => {
        setCurrentFolderId(folder.id);
        setBreadcrumbs(prev => [...prev, { id: folder.id, name: folder.name || 'Unnamed Folder' }]);
    };

    const handleBreadcrumbClick = (index) => {
        const target = breadcrumbs[index];
        setCurrentFolderId(target.id);
        setBreadcrumbs(prev => prev.slice(0, index + 1));
    };

    const renderTabs = () => (
        <div style={styles.tabContainer}>
            <button
                style={{ ...styles.tabButton, ...(activeTab === 'documentCloud' ? styles.activeTabButton : {}) }}
                onClick={() => setActiveTab('documentCloud')}
            >
                Staff Document Cloud
            </button>
            <button
                style={{ ...styles.tabButton, ...(activeTab === 'riskAssessments' ? styles.activeTabButton : {}) }}
                onClick={() => setActiveTab('riskAssessments')}
            >
                Employee Risk Assessments
            </button>
            <button
                style={{ ...styles.tabButton, ...(activeTab === 'dbsCertification' ? styles.activeTabButton : {}) }}
                onClick={() => setActiveTab('dbsCertification')}
            >
                DBS Certification
            </button>
        </div>
    );

    const renderDocumentCloud = () => {
        if (loading) return <p style={styles.loadingText}>Loading documents...</p>;

        return (
            <div style={styles.contentSection}>
                {/* Breadcrumbs */}
                <div style={styles.breadcrumbsContainer}>
                    {breadcrumbs.map((crumb, index) => (
                        <span key={index} style={styles.breadcrumbItem}>
                            <span 
                                style={{ ...styles.breadcrumbLink, ...(index === breadcrumbs.length - 1 ? styles.breadcrumbActive : {}) }}
                                onClick={() => handleBreadcrumbClick(index)}
                            >
                                {crumb.name}
                            </span>
                            {index < breadcrumbs.length - 1 && <span style={styles.breadcrumbSeparator}> / </span>}
                        </span>
                    ))}
                </div>

                {/* Toolbar */}
                {canEdit && (
                    <div style={styles.toolbar}>
                        <button style={styles.primaryButton}>+ New Folder</button>
                        <button style={styles.primaryButton}>+ Upload File</button>
                    </div>
                )}

                {/* Files Grid */}
                {currentDocuments.length === 0 ? (
                    <p style={styles.emptyText}>This folder is empty.</p>
                ) : (
                    <div style={styles.gridContainer}>
                        {currentDocuments.map(doc => {
                            const isFolder = doc.contentType === 'folder';
                            return (
                                <div 
                                    key={doc.id} 
                                    style={styles.fileCard}
                                    onClick={() => {
                                        if (isFolder) {
                                            handleFolderClick(doc);
                                        } else if (doc.fileURL) {
                                            window.open(doc.fileURL, '_blank');
                                        }
                                    }}
                                >
                                    <div style={styles.fileIconContainer}>
                                        {isFolder ? (
                                            <span style={{ fontSize: '32px' }}>📁</span>
                                        ) : (
                                            <span style={{ fontSize: '32px' }}>📄</span>
                                        )}
                                    </div>
                                    <div style={styles.fileDetails}>
                                        <h4 style={styles.fileName}>{doc.name || 'Unnamed Document'}</h4>
                                        <p style={styles.fileMeta}>
                                            {isFolder ? 'Folder' : `${((doc.size || 0) / 1024).toFixed(1)} KB`} 
                                            {doc.createdAt && ` • ${doc.createdAt.toDate().toLocaleDateString()}`}
                                        </p>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>
        );
    };

    const renderRiskAssessments = () => {
        if (loading) return <p style={styles.loadingText}>Loading risk assessments...</p>;

        if (riskAssessments.length === 0) {
            return <p style={styles.emptyText}>No risk assessments found for this employee.</p>;
        }

        return (
            <div style={styles.contentSection}>
                {canEdit && (
                    <div style={{ marginBottom: '15px', textAlign: 'right' }}>
                        <button style={styles.primaryButton}>+ New Risk Assessment</button>
                    </div>
                )}
                <div style={styles.listContainer}>
                    {riskAssessments.map(risk => {
                        const mode = risk.currentRiskAssess?.mode || 'Unknown';
                        let modeColor = '#6b7280';
                        if (mode === 'High') modeColor = '#ef4444';
                        if (mode === 'Medium') modeColor = '#f59e0b';
                        if (mode === 'Low') modeColor = '#10b981';

                        return (
                            <div key={risk.id} style={styles.listCard}>
                                <div style={styles.listCardHeader}>
                                    <h4 style={styles.listCardTitle}>{risk.riskName || risk.riskType || 'Risk Assessment'}</h4>
                                    <span style={{ ...styles.badge, backgroundColor: modeColor }}>
                                        {mode} Risk
                                    </span>
                                </div>
                                <div style={styles.listCardBody}>
                                    <div style={styles.detailRow}>
                                        <span style={styles.detailLabel}>Created At:</span>
                                        <span style={styles.detailValue}>
                                            {risk.createdAt ? risk.createdAt.toDate().toLocaleDateString() : 'N/A'}
                                        </span>
                                    </div>
                                    <div style={styles.detailRow}>
                                        <span style={styles.detailLabel}>Review Date:</span>
                                        <span style={styles.detailValue}>
                                            {risk.reviewDate ? risk.reviewDate.toDate().toLocaleDateString() : 'N/A'}
                                        </span>
                                    </div>
                                    <div style={styles.detailRow}>
                                        <span style={styles.detailLabel}>Archived:</span>
                                        <span style={styles.detailValue}>
                                            {risk.archived ? 'Yes' : 'No'}
                                        </span>
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            </div>
        );
    };

    const renderDBSCertification = () => {
        const dbs = employee?.dbs || {};
        
        return (
            <div style={styles.contentSection}>
                <div style={styles.dbsCard}>
                    <h3 style={styles.dbsCardTitle}>Current DBS Information</h3>
                    
                    <div style={styles.dbsGrid}>
                        <div style={styles.dbsField}>
                            <span style={styles.dbsLabel}>Service ID</span>
                            <span style={styles.dbsValue}>{dbs.serviceId || 'Not provided'}</span>
                        </div>
                        <div style={styles.dbsField}>
                            <span style={styles.dbsLabel}>Expiry Date</span>
                            <span style={styles.dbsValue}>{dbs.expiryDate || 'No expiry set'}</span>
                        </div>
                        <div style={styles.dbsField}>
                            <span style={styles.dbsLabel}>Update Service Link</span>
                            {dbs.updateServiceLink ? (
                                <a href={dbs.updateServiceLink} target="_blank" rel="noreferrer" style={styles.link}>
                                    {dbs.updateServiceLink}
                                </a>
                            ) : (
                                <span style={styles.dbsValue}>Not provided</span>
                            )}
                        </div>
                        <div style={styles.dbsField}>
                            <span style={styles.dbsLabel}>DBS Certificate</span>
                            {dbs.file ? (
                                <a href={dbs.file} target="_blank" rel="noreferrer" style={styles.fileLinkButton}>
                                    View Document
                                </a>
                            ) : (
                                <span style={styles.dbsValue}>No file uploaded</span>
                            )}
                        </div>
                    </div>
                </div>

                {canEdit && (
                    <div style={styles.uploadSection}>
                        <h4 style={styles.uploadTitle}>Update DBS Certificate</h4>
                        <div style={styles.uploadForm}>
                            <input type="file" style={styles.fileInput} />
                            <button style={styles.primaryButton}>Upload New DBS</button>
                        </div>
                    </div>
                )}
            </div>
        );
    };

    return (
        <div style={styles.container}>
            {renderTabs()}
            <div style={styles.tabContent}>
                {activeTab === 'documentCloud' && renderDocumentCloud()}
                {activeTab === 'riskAssessments' && renderRiskAssessments()}
                {activeTab === 'dbsCertification' && renderDBSCertification()}
            </div>
        </div>
    );
};

const styles = {
    container: {
        backgroundColor: '#ffffff',
        borderRadius: '12px',
        boxShadow: '0 4px 6px rgba(0, 0, 0, 0.05)',
        border: '1px solid #f3f4f6',
        overflow: 'hidden',
        marginTop: '20px'
    },
    tabContainer: {
        display: 'flex',
        backgroundColor: '#f8fafc',
        borderBottom: '1px solid #e2e8f0',
        padding: '0 10px',
    },
    tabButton: {
        padding: '16px 20px',
        fontSize: '14px',
        fontWeight: '600',
        color: '#64748b',
        backgroundColor: 'transparent',
        border: 'none',
        borderBottom: '3px solid transparent',
        cursor: 'pointer',
        transition: 'all 0.2s',
    },
    activeTabButton: {
        color: '#0f172a',
        borderBottomColor: '#3b82f6',
    },
    tabContent: {
        padding: '24px',
        minHeight: '300px',
    },
    contentSection: {
        display: 'flex',
        flexDirection: 'column',
        gap: '20px',
    },
    breadcrumbsContainer: {
        padding: '12px 16px',
        backgroundColor: '#f1f5f9',
        borderRadius: '8px',
        fontSize: '14px',
        display: 'flex',
        alignItems: 'center',
        flexWrap: 'wrap'
    },
    breadcrumbItem: {
        display: 'flex',
        alignItems: 'center',
    },
    breadcrumbLink: {
        color: '#3b82f6',
        cursor: 'pointer',
        fontWeight: '500',
    },
    breadcrumbActive: {
        color: '#0f172a',
        cursor: 'default',
    },
    breadcrumbSeparator: {
        margin: '0 8px',
        color: '#94a3b8',
    },
    toolbar: {
        display: 'flex',
        justifyContent: 'flex-end',
        gap: '12px',
        marginBottom: '10px'
    },
    primaryButton: {
        padding: '8px 16px',
        backgroundColor: '#3b82f6',
        color: 'white',
        border: 'none',
        borderRadius: '6px',
        fontSize: '14px',
        fontWeight: '600',
        cursor: 'pointer',
        transition: 'background-color 0.2s',
    },
    gridContainer: {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))',
        gap: '16px',
    },
    fileCard: {
        display: 'flex',
        alignItems: 'center',
        gap: '16px',
        padding: '16px',
        backgroundColor: '#ffffff',
        border: '1px solid #e2e8f0',
        borderRadius: '10px',
        cursor: 'pointer',
        transition: 'transform 0.2s, box-shadow 0.2s',
    },
    fileIconContainer: {
        width: '48px',
        height: '48px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: '#f8fafc',
        borderRadius: '8px',
    },
    fileDetails: {
        flex: 1,
        overflow: 'hidden'
    },
    fileName: {
        margin: '0 0 4px 0',
        fontSize: '15px',
        fontWeight: '600',
        color: '#1e293b',
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
    },
    fileMeta: {
        margin: 0,
        fontSize: '13px',
        color: '#64748b',
    },
    listContainer: {
        display: 'flex',
        flexDirection: 'column',
        gap: '16px',
    },
    listCard: {
        padding: '20px',
        backgroundColor: '#ffffff',
        border: '1px solid #e2e8f0',
        borderRadius: '10px',
        display: 'flex',
        flexDirection: 'column',
        gap: '12px',
        transition: 'box-shadow 0.2s',
    },
    listCardHeader: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        borderBottom: '1px solid #f1f5f9',
        paddingBottom: '12px',
    },
    listCardTitle: {
        margin: 0,
        fontSize: '16px',
        fontWeight: '600',
        color: '#1e293b',
    },
    badge: {
        padding: '4px 10px',
        borderRadius: '20px',
        fontSize: '12px',
        fontWeight: '700',
        color: '#ffffff',
    },
    listCardBody: {
        display: 'flex',
        flexDirection: 'column',
        gap: '8px',
    },
    detailRow: {
        display: 'flex',
        justifyContent: 'space-between',
        fontSize: '14px',
    },
    detailLabel: {
        color: '#64748b',
        fontWeight: '500',
    },
    detailValue: {
        color: '#0f172a',
        fontWeight: '600',
    },
    dbsCard: {
        padding: '24px',
        backgroundColor: '#ffffff',
        border: '1px solid #e2e8f0',
        borderRadius: '10px',
    },
    dbsCardTitle: {
        margin: '0 0 20px 0',
        fontSize: '18px',
        fontWeight: '700',
        color: '#0f172a',
        borderBottom: '2px solid #f1f5f9',
        paddingBottom: '10px'
    },
    dbsGrid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
        gap: '24px',
    },
    dbsField: {
        display: 'flex',
        flexDirection: 'column',
        gap: '8px',
    },
    dbsLabel: {
        fontSize: '13px',
        fontWeight: '600',
        color: '#64748b',
        textTransform: 'uppercase',
        letterSpacing: '0.5px'
    },
    dbsValue: {
        fontSize: '15px',
        fontWeight: '600',
        color: '#1e293b',
    },
    link: {
        fontSize: '15px',
        color: '#3b82f6',
        textDecoration: 'none',
        fontWeight: '500',
        wordBreak: 'break-all'
    },
    fileLinkButton: {
        padding: '8px 16px',
        backgroundColor: '#f1f5f9',
        color: '#3b82f6',
        border: '1px solid #cbd5e1',
        borderRadius: '6px',
        fontSize: '14px',
        fontWeight: '600',
        cursor: 'pointer',
        textAlign: 'center',
        textDecoration: 'none',
        display: 'inline-block',
        width: 'fit-content'
    },
    uploadSection: {
        padding: '24px',
        backgroundColor: '#f8fafc',
        border: '1px dashed #cbd5e1',
        borderRadius: '10px',
        marginTop: '20px'
    },
    uploadTitle: {
        margin: '0 0 16px 0',
        fontSize: '16px',
        fontWeight: '600',
        color: '#1e293b',
    },
    uploadForm: {
        display: 'flex',
        alignItems: 'center',
        gap: '16px',
        flexWrap: 'wrap'
    },
    fileInput: {
        padding: '8px',
        border: '1px solid #cbd5e1',
        borderRadius: '6px',
        backgroundColor: '#ffffff',
        fontSize: '14px'
    },
    loadingText: {
        textAlign: 'center',
        color: '#64748b',
        padding: '40px 0',
        fontSize: '15px',
    },
    emptyText: {
        textAlign: 'center',
        color: '#94a3b8',
        padding: '40px 0',
        fontSize: '15px',
        fontStyle: 'italic',
        border: '1px dashed #e2e8f0',
        borderRadius: '8px',
        backgroundColor: '#f8fafc'
    }
};

export default EmployeeDocumentsModule;
